// Report It API client (v7 §6.1, MV-1077). Submits a user incident report —
// rough symptom text + correlation ids + the captured client snapshot — to the
// observability-analysis Lambda's /reports endpoint.
//
// Uses the shared $api instance so every call carries the JWT (auth) and the
// X-Mytaverse-Correlation-Id header (the cross-system id the backend stamps on
// its logs), exactly as the rest of the observability integration does.

import $api from '../../http/axios';
import { ReportItClientSnapshot } from './snapshotTypes';

// Relative to the $api baseURL (env.REACT_APP_API_URL). Mirrors the backend's
// getObservabilityReportsApiBasePath() = '/api/v2/observability/reports'.
const REPORTS_PATH = 'api/v2/observability/reports';

export type ReportIncidentType =
  | 'black_screen'
  | 'audio_only'
  | 'frozen_video'
  | 'disconnect'
  | 'other';

export type ReportSymptomHint =
  | 'screen_black'
  | 'audio_still_playing'
  | 'video_frozen'
  | 'error_popup'
  | 'controls_not_working'
  | 'disconnected'
  | 'other';

export type ReportSeverityHint = 'low' | 'medium' | 'high';

/** Non-identifying correlation ids the client may attach (v7 §13 — identity is
 *  NOT sent from the client; the backend derives it from auth). */
export interface ReportCorrelationEntities {
  browserSessionId?: string;
  sentryTraceId?: string;
  websocketSessionId?: string;
  streamSessionId?: string;
  streamGroupId?: string;
  /** AWS region of the GameLift Streams session. */
  region?: string;
  placementId?: string;
  instanceId?: string;
  requestId?: string;
}

/** How the user added a screenshot — surfaced to the backend manifest (v7). */
export type ReportAttachmentSource = 'manual_upload' | 'paste' | 'drag_drop';

export interface ReportAttachmentDeclaration {
  filename?: string;
  contentType: string;
  byteSize: number;
  source?: ReportAttachmentSource;
}

export interface ReportAttachmentFile {
  file: File;
  filename?: string;
  contentType: string;
  byteSize: number;
  source?: ReportAttachmentSource;
}

export interface ReportAttachmentPolicy {
  attachmentsEnabled: boolean;
  inAppCaptureEnabled: boolean;
  ocrEnabled: boolean;
  visualClassificationEnabled: boolean;
  tenantPolicyVersion: string;
  approvalId?: string;
  approvedProcessorRegion?: string;
}

export interface SubmitReportInput {
  environment: string;
  eventId?: string;
  /** Idempotency inputs (v7 §10.3) — combined server-side with the auth tenant. */
  browserSessionId: string;
  clientReportNonce: string;

  incidentType: ReportIncidentType;
  occurredAt: string;
  userReportedAt?: string;
  description?: string;
  symptomHints?: ReportSymptomHint[];
  severityHint?: ReportSeverityHint;
  userLocalTime?: string;
  userTimeZone?: string;
  language?: string;
  attachDiagnostics?: boolean;

  entities?: ReportCorrelationEntities;
  attachments?: ReportAttachmentDeclaration[];
  clientSnapshot?: ReportItClientSnapshot;
  /** Web Hub build version (MV-1396). */
  hubVersion?: string;
  /** Spatial Manager service version the client saw (MV-1396). */
  spatialManagerVersion?: string;
  /** Scrubbed, size-capped tail of the browser console log (MV-1396). */
  browserLogs?: string;
}

export interface PresignedAttachmentUpload {
  attachmentId: string;
  uploadUrl: string;
  headers?: Record<string, string>;
  /** Declaration order from the backend manifest — used to pair each file to
   *  its upload target deterministically (not by array position). */
  index?: number;
}

export interface SubmitReportResponse {
  reportId: string;
  status: string;
  created: boolean;
  createdAt: string;
  attachmentUploads?: PresignedAttachmentUpload[];
}

export interface ReportPolicyResponse {
  tenantId: string;
  environment: string;
  attachmentPolicy: ReportAttachmentPolicy;
}

/**
 * Build the POST /reports body (pure). Only present, non-empty fields are
 * included — the backend validator treats most as optional and rejects unknown
 * shapes, so we keep the payload tight.
 */
export const buildReportBody = (
  input: SubmitReportInput,
): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    environment: input.environment,
    browserSessionId: input.browserSessionId,
    clientReportNonce: input.clientReportNonce,
    incidentType: input.incidentType,
    occurredAt: input.occurredAt,
  };

  if (input.eventId) {
    body.eventId = input.eventId;
  }

  if (input.userReportedAt) {
    body.userReportedAt = input.userReportedAt;
  }

  if (input.description) {
    body.description = input.description;
  }

  if (input.symptomHints && input.symptomHints.length > 0) {
    body.symptomHints = input.symptomHints;
  }

  if (input.severityHint) {
    body.severityHint = input.severityHint;
  }

  if (input.userLocalTime) {
    body.userLocalTime = input.userLocalTime;
  }

  if (input.userTimeZone) {
    body.userTimeZone = input.userTimeZone;
  }

  if (input.language) {
    body.language = input.language;
  }

  if (input.attachDiagnostics) {
    body.attachDiagnostics = true;
  }

  if (input.entities && Object.keys(input.entities).length > 0) {
    body.entities = input.entities;
  }

  if (input.attachments && input.attachments.length > 0) {
    body.attachments = input.attachments;
  }

  if (input.clientSnapshot) {
    body.clientSnapshot = input.clientSnapshot;
  }

  if (input.hubVersion) {
    body.hubVersion = input.hubVersion;
  }

  if (input.spatialManagerVersion) {
    body.spatialManagerVersion = input.spatialManagerVersion;
  }

  if (input.browserLogs) {
    body.browserLogs = input.browserLogs;
  }

  return body;
};

/** Submit a report. Returns the created (or idempotently-resolved) report. */
export const submitReport = async (
  input: SubmitReportInput,
): Promise<SubmitReportResponse> => {
  const { data } = await $api.post<SubmitReportResponse>(
    REPORTS_PATH,
    buildReportBody(input),
  );

  return data;
};

export const getReportPolicy = async (): Promise<ReportPolicyResponse> => {
  const { data } = await $api.get<ReportPolicyResponse>(`${REPORTS_PATH}/policy`);
  return data;
};

export const buildAttachmentDeclarations = (
  files: ReportAttachmentFile[] | undefined,
): ReportAttachmentDeclaration[] | undefined => {
  if (!files || files.length === 0) {
    return undefined;
  }

  return files.map((attachment) => ({
    filename: attachment.filename || attachment.file.name || undefined,
    contentType: attachment.contentType,
    byteSize: attachment.byteSize,
    source: attachment.source,
  }));
};

export interface AttachmentPutArgs {
  url: string;
  file: File;
  headers: Record<string, string>;
  /** Reports upload progress as a 0..1 fraction. */
  onProgress?: (fraction: number) => void;
}

export type AttachmentPut = (args: AttachmentPutArgs) => Promise<void>;

// Default PUT via XMLHttpRequest — unlike fetch, XHR exposes real upload
// progress events, which drives the per-file progress bars. Resolves on a 2xx.
const xhrAttachmentPut: AttachmentPut = ({ url, file, headers, onProgress }) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);

    Object.entries(headers).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });

    if (xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (onProgress && event.lengthComputable && event.total > 0) {
          onProgress(event.loaded / event.total);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve();
      } else {
        reject(new Error(`Attachment upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Attachment upload network error'));
    xhr.onabort = () => reject(new Error('Attachment upload aborted'));

    xhr.send(file);
  });

export interface UploadReportAttachmentsOptions {
  /** Per-file upload progress (0..1), keyed by declaration index. */
  onProgress?: (index: number, fraction: number) => void;
  /** Injectable PUT for tests; defaults to an XHR upload with progress. */
  putFile?: AttachmentPut;
}

export const uploadReportAttachments = async (
  files: ReportAttachmentFile[] | undefined,
  uploads: PresignedAttachmentUpload[] | undefined,
  options: UploadReportAttachmentsOptions = {},
): Promise<void> => {
  if (!files || files.length === 0) {
    return;
  }

  if (!uploads || uploads.length !== files.length) {
    throw new Error('Report attachment upload targets were not returned');
  }

  const putFile = options.putFile ?? xhrAttachmentPut;
  const { onProgress } = options;

  await Promise.all(
    files.map((attachment, index) => {
      // Pair by the backend's declaration index when present, falling back to
      // array position for backends that don't echo it.
      const target =
        uploads.find((upload) => upload.index === index) ?? uploads[index];

      if (!target) {
        return Promise.reject(
          new Error(
            `No upload target for attachment ${attachment.filename ?? index + 1}`,
          ),
        );
      }

      const headers: Record<string, string> = {
        'Content-Type': attachment.contentType,
        ...(target.headers ?? {}),
      };

      return putFile({
        url: target.uploadUrl,
        file: attachment.file,
        headers,
        onProgress: onProgress
          ? (fraction) => onProgress(index, fraction)
          : undefined,
      });
    }),
  );
};

/**
 * Ask the backend to (re)run analysis for a stored report — used after the
 * client snapshot has been uploaded, or to refresh a verdict (v7 §6.3).
 */
export const requestReportAnalysis = async (
  reportId: string,
): Promise<unknown> => {
  const { data } = await $api.post(
    `${REPORTS_PATH}/${encodeURIComponent(reportId)}/analyse`,
    {},
  );

  return data;
};

/** Best-effort local context for the report (impure — reads browser globals). */
export const collectBrowserReportContext = (): {
  userLocalTime: string;
  userTimeZone?: string;
  language?: string;
} => {
  const context: {
    userLocalTime: string;
    userTimeZone?: string;
    language?: string;
  } = { userLocalTime: new Date().toString() };

  try {
    context.userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Intl unavailable — omit the time zone.
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    context.language = navigator.language;
  }

  return context;
};
